import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { useChartColors } from "./chartColors";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import { HW_TINT_INK } from "./theme";
import type { AnalyticsEnvelope, RollupGroup, AnalyticsViewProps } from "./types";

type RollupData = AnalyticsEnvelope & { by: string; groups: RollupGroup[] };

export function RollupComparisonView({
  data,
  loading,
  error,
  by: byProp,
  onByChange,
}: AnalyticsViewProps<RollupData> & {
  by?: "team" | "role";
  onByChange?: (by: "team" | "role") => void;
}) {
  const { t } = useTranslation();
  const colors = useChartColors();
  const [byState, setByState] = useState<"team" | "role">("team");
  const by = byProp ?? byState;
  const setBy = onByChange ?? setByState;
  const showToggle = typeof onByChange === "function";

  const groups = (data?.groups ?? []).map((g) => ({
    key: g.key,
    hours: Math.round(g.worked_seconds / 3600),
    delta: Math.round(g.delta_worked_seconds / 3600),
  }));
  const toggle = showToggle ? (
    <div className="flex gap-1 text-[12px]">
      {(["team", "role"] as const).map((b) => (
        <button
          key={b}
          type="button"
          aria-pressed={by === b}
          onClick={() => setBy(b)}
          className={by === b ? "font-semibold underline" : "text-muted-foreground"}
        >
          {t(`homework.analytics.rollup.${b}`)}
        </button>
      ))}
    </div>
  ) : undefined;

  return (
    <Panel title={t("homework.analytics.rollup.title")} action={toggle}>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !groups.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : (
        <>
          <div role="img" aria-label={t("homework.analytics.rollup.title")}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={groups} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={colors.grid} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="key" stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} />
                <YAxis stroke={colors.axis} tick={{ fontSize: 12, fill: colors.axis }} width={36} />
                <Tooltip />
                <Bar
                  dataKey="hours"
                  fill={colors.series(0)}
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-0.5 text-[12px]">
            {groups.map((g) => (
              <li key={g.key} className="flex justify-between">
                <span>{g.key}</span>
                <span className={g.delta >= 0 ? HW_TINT_INK.success : HW_TINT_INK.danger}>
                  {g.delta >= 0 ? "▲" : "▼"} {Math.abs(g.delta)}h vs prior
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
